import {
  HealthResponse,
  ApiErrorResponse,
} from '@pkg/shared'

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api'

// Debug logging
const DEBUG_API = import.meta.env.VITE_DEBUG_API === 'true' || import.meta.env.DEV

function debugLog(message: string, data?: any) {
  if (DEBUG_API) {
    console.log(`[API DEBUG] ${message}`, data)
  }
}


class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status?: number
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {},
  timeoutMs?: number
): Promise<T> {
  const fullUrl = `${API_BASE}${endpoint}`

  debugLog('Making API request', {
    url: fullUrl,
    endpoint,
    apiBase: API_BASE,
    method: options.method || 'GET',
    hasBody: options.body !== undefined,
    env: {
      VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
      VITE_DEBUG_API: import.meta.env.VITE_DEBUG_API,
      DEV: import.meta.env.DEV,
      MODE: import.meta.env.MODE,
    },
  })

  // Не добавляем Content-Type если тело запроса пустое или заголовки уже переопределены
  const hasBody = options.body !== undefined
  const hasContentType =
    options.headers && 'Content-Type' in (options.headers as Record<string, string>)

  const headers: Record<string, string> = {
    ...(hasBody && !hasContentType ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string>),
  }

  // Создаем AbortController для таймаута
  const controller = new AbortController()
  let timeoutId: NodeJS.Timeout | undefined

  if (timeoutMs) {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  }

  try {
    debugLog('Fetching URL', { fullUrl, headers, options })

    const response = await fetch(fullUrl, {
      headers,
      signal: controller.signal,
      ...options,
    })

    debugLog('Response received', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
      url: response.url,
    })

    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    const data = await response.json()

    if (!response.ok) {
      debugLog('Error response data', data)
      const error = data as ApiErrorResponse
      throw new ApiError(error.code, error.message, response.status)
    }

    debugLog('Success response data', data)
    return data as T
  } catch (err) {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    debugLog('Request failed', {
      error: err instanceof Error ? err.message : String(err),
      name: err instanceof Error ? err.name : 'Unknown',
      stack: err instanceof Error ? err.stack : undefined,
    })

    if (err instanceof Error && err.name === 'AbortError') {
      throw new ApiError('TIMEOUT', 'Превышено время ожидания ответа сервера', 408)
    }

    throw err
  }
}

export const api = {
  // Health check
  getHealth: (): Promise<HealthResponse> => fetchApi('/health'),
}

export { ApiError }